<template>
        <o-modal @close="handleCancel" modalClass="redeem-modal-wide">
            <template v-slot:header>
                {{ voucherRedeem.cardTitle }}
            </template>
            <template v-slot:body>
                <o-form autocomplete="off">
                    <o-form-group title="" name="state" :label="false" class="m-0 p-0">
                        <!-- Two-column container -->
                        <div class="two-col-layout">
                            <!-- ===== LEFT PANEL: Select Member ===== -->
                            <div class="left-panel">
                                <p class="panel-title">Select Member</p>
                                <template v-if="voucherRedeem.member_id && voucherRedeem.cardTitle === 'Claim Voucher'">
                                    <div class="form-sub-item">
                                        <label>Member Name <span class="required">*</span></label>
                                        <o-input-text v-model="voucherRedeem.member_name" readonly />
                                    </div>
                                </template>
                                <template v-else>
                                    <div class="member-search-row">
                                        <div class="member-search-wrap">
                                            <i class="fas fa-search search-icon"></i>
                                            <input type="text" class="member-search-input" v-model="memberSearch"
                                                placeholder="Search by name or email" />
                                            <button v-if="memberSearch" class="search-clear-btn" @click="clearMemberSearch"
                                                type="button">&times;</button>
                                        </div>
                                        <button type="button" class="btn-scan" data-bs-toggle="modal"
                                            data-bs-target="#scanner-modal-redeem" @click="startScan" title="Scan QR Code">
                                            <i class="fas fa-qrcode"></i>
                                        </button>
                                    </div>

                                    <!-- Member list -->
                                    <div class="member-list-scroll" ref="membersListRef" @scroll="handleMemberScroll">
                                        <div v-if="!membersLoaded" class="member-list-empty">
                                            <i class="fas fa-spinner fa-spin"></i> Loading members...
                                        </div>
                                        <div v-else-if="displayMembers.length === 0" class="member-list-empty">
                                            No members found
                                        </div>
                                        <div v-else v-for="member in orderedDisplayMembers" :key="member.id"
                                            class="member-card" :class="{ selected: voucherRedeem.member_id === member.id }"
                                            @click="selectMember(member)">
                                            <div class="member-card-content">
                                                <div class="member-avatar">
                                                    <img v-if="member.photo && !member.imageError" :src="member.photo"
                                                        :alt="member.full_name" class="avatar-img"
                                                        @error="member.imageError = true" />
                                                    <span v-else class="avatar-initials">{{ getInitials(member.full_name) }}</span>
                                                </div>
                                                <div class="member-info">
                                                    <p class="member-name">{{ member.full_name }}</p>
                                                    <p class="member-email">{{ member.email }}</p>
                                                    <p class="member-code">{{ member.code }}</p>
                                                </div>
                                            </div>
                                            <div class="member-check-wrap">
                                                <div v-if="voucherRedeem.member_id === member.id" class="member-check">
                                                    <i class="fas fa-check"></i>
                                                </div>
                                                <div v-else class="member-radio"></div>
                                            </div>
                                        </div>
                                        <div v-if="loadingMore" class="member-list-empty">
                                            <i class="fas fa-spinner fa-spin"></i> Loading members...
                                        </div>
                                    </div>
                                </template>

                                <div v-if="validation.member_id" class="error-text mt-1">
                                    {{ validation.member_id[0] }}
                                </div>

                                <!-- Member Points Warning -->
                                <o-member-points-warning 
                                    v-if="selectedMember" 
                                    :member="selectedMember" 
                                />
                            </div>

                            <!-- ===== RIGHT PANEL: Voucher Details ===== -->
                            <div class="right-panel">
                                <p class="panel-title">Voucher Details</p>
                                <div class="form-sub-item">
                                    <label>Voucher Name <span class="required">*</span></label>
                                    <o-lookup-voucher 
                                        v-if="selectedBusinessProfile.merchant_id != '' && voucherRedeem.cardTitle === 'Create Voucher Redeem'" 
                                        v-model="voucherRedeem.voucher_id" 
                                        :branchid="selectedBusinessProfile.branch_id" 
                                        :merchantid="selectedBusinessProfile.merchant_id" 
                                        class="modern-v-select"
                                        placeholder="-- Select Voucher --"
                                        @loaded="disableLoading()"
                                        @option:selected="getExpired"/>
                                    
                                    <o-input-text 
                                        v-else-if="voucherRedeem.cardTitle === 'Claim Voucher'" 
                                        v-model="voucherRedeem.voucher_name" 
                                        readonly/>
                                    
                                    <div v-if="validation.voucher_id" class="error-text mt-1">{{ validation.voucher_id[0] }}</div>
                                </div>

                                <div v-if="voucherRedeem.cardTitle === 'Claim Voucher'" class="form-sub-item">
                                    <label>Provided By <span class="required">*</span></label>
                                    <o-input-text 
                                        v-model="voucherRedeem.voucher_merchant_name" 
                                        readonly/>
                                    
                                    <div v-if="validation.voucher_merchant_name" class="error-text mt-1">{{ validation.voucher_merchant_name[0] }}</div>
                                </div>

                                <div v-if="!voucherRedeem.claimed_at_buttom" class="form-sub-item">
                                    <label>Quantity <span class="required">*</span></label>
                                    <o-input-text 
                                        name="quantity" 
                                        :autocomplete="'off'" 
                                        v-model="voucherRedeem.quantity" 
                                        type="number" 
                                        min="1" 
                                        max="10" 
                                        :required="true"
                                    />
                                    <div v-if="validation.quantity" class="error-text mt-1">{{ validation.quantity[0] }}</div>
                                </div>
                            </div>
                        </div>

                        <!-- Footer — outside two-col-layout, matching InjectPointForm -->
                        <div class="form-footer">
                            <!-- Error Alert inside Footer -->
                            <div v-if="error" class="error-alert-footer">
                                <i class="fas fa-exclamation-circle error-alert-icon"></i>
                                <div>
                                    <p class="error-alert-title">{{ error }}</p>
                                </div>
                            </div>
                            <div class="footer-actions">
                                <button type="button" class="btn-cancel" @click="handleCancel">Cancel</button>

                                <!-- Claim buttons -->
                                <template v-if="voucherRedeem.claimed_at_buttom">
                                    <template v-if="selectedVoucherType === 'credit_discount'">
                                        <button 
                                            type="button"
                                            class="btn-confirm btn-confirm-pos" 
                                            @click="goToPOS"
                                        >
                                            <i class="fas fa-store me-1"></i> Claim at POS
                                        </button>
                                    </template>
                                    <button 
                                        type="button"
                                        class="btn-confirm" 
                                        @click="selectedVoucherType === 'credit_discount' ? handleManualClaimCreditDiscount() : handleClaimVoucher()" 
                                        :disabled="isMemberPointsNegative || voucherRedeem.isLoading"
                                    >
                                        <i v-if="voucherRedeem.isLoading" class="fas fa-spinner fa-spin"></i>
                                        <i v-else class="fas fa-ticket-alt me-1"></i>
                                        {{ isMemberPointsNegative ? 'Cannot Claim' : (voucherRedeem.isLoading ? 'Claiming...' : (selectedVoucherType === 'credit_discount' ? 'Manual Claim' : 'Claim Now')) }}
                                    </button>
                                </template>

                                <!-- Redeem buttons -->
                                <template v-if="voucherRedeem.redeem_claimed">
                                    <button 
                                        type="button" 
                                        class="btn-confirm btn-confirm-success" 
                                        @click="redeemClaim()" 
                                        :disabled="isMemberPointsNegative || voucherRedeem.isLoading"
                                    >
                                        {{ selectedVoucherType === 'credit_discount' ? 'Redeem & Manual Claimed' : 'Redeem & Claimed' }}
                                    </button>
                                    <button 
                                        v-if="selectedVoucherType === 'credit_discount'"
                                        type="button" 
                                        class="btn-confirm btn-confirm-pos" 
                                        @click="redeemToPOS()" 
                                        :disabled="isMemberPointsNegative || voucherRedeem.isLoading"
                                    >
                                        Redeem & Go to POS
                                    </button>
                                    <button 
                                        type="button" 
                                        class="btn-confirm" 
                                        @click="redeem()" 
                                        :disabled="isMemberPointsNegative || voucherRedeem.isLoading"
                                    >
                                        <i v-if="voucherRedeem.isLoading" class="fas fa-spinner fa-spin"></i>
                                        {{ isMemberPointsNegative ? 'Cannot Redeem' : (voucherRedeem.isLoading ? 'Processing...' : 'Redeem') }}
                                    </button>
                                </template>
                            </div>
                        </div>
                    </o-form-group>
                </o-form>
            </template>
        </o-modal>

    <!-- Scanner Modal — teleported to body, z-index above OModal (9999) -->
    <teleport to="body">
        <div v-if="!scanHidden" class="scan-overlay" @click.self="stopScan">
            <div class="scan-modal-box">
                <div class="scan-modal-header">
                    <span class="scan-modal-title">Scanning</span>
                    <button type="button" class="scan-modal-close" @click="stopScan" aria-label="Close">
                        <i class="fas fa-times"></i>
                    </button>
                </div>
                <div class="scan-modal-body">
                    <div id="wrap-scanner-redeem"></div>
                    <div class="scan-message" v-if="error_scan.message != ''">{{ error_scan.message }}</div>
                </div>
                <div class="scan-modal-footer">
                    <button type="button" class="btn-stop-scan" @click="stopScan">Stop scanning</button>
                </div>
            </div>
        </div>
    </teleport>

</template>

<script>
import { OForm, OFormGroup, OInputText, OLookupVoucher, OMemberPointsWarning, OModal } from '@/components/OmnihControl';
import { Html5Qrcode } from 'html5-qrcode';
import { mapState } from 'vuex';
import dayjs from 'dayjs';
import store from '@/store'

export default {
    name: 'AddVoucherRedeem',
    components: {
        OForm,
        OFormGroup,
        OInputText,
        OLookupVoucher,
        OMemberPointsWarning,
        OModal
    },
    props: {
        isModal: {
            type: Boolean,
            default: false
        },
        redeemId: {
            type: String,
            default: null
        }
    },
    emits: ['success', 'cancel'],
    data: function() {
        return{
            selectedOption: [],
            items: [],
            vouchers: [],
            members: [],
            selectedBusinessProfile: ({
                corporate_id: '',
                branch_id: '',
                merchant_id: ''
            }),
            voucherRedeem: ({
                merchant_id: '',
                quantity: '1',
                voucher_id: '',
                member_id: '',
                voucher_name: '',
                member_name: '',
                voucher_merchant_name: '',
                expired_at: '',
                claimed_at: '',
                disable: false,
                isLoading: false,
                cardTitle: 'Create Voucher Redeem',
                claimed_at_buttom: false,
                redeem_claimed: true
            }),
            options: [],
            validation: [],
            error: '',
            // Member search & scan state
            memberSearch: '',
            membersLoaded: false,
            displayMembers: [],
            selectedMemberObj: null,
            selectedViaSearch: false,
            loadingMore: false,
            scanHidden: true,
            error_scan: { message: '' },
            scanner: null,
            cameraId: '',
            corporateId: '',
            searchDebounceTimer: null,
            preloadAbort: false,
        }
    },
    computed: {
        ...mapState({

        }),
        currentRouteName() {
            return this.$route.name;
        },
        selectedMember() {
            // Check from displayMembers first (new member card list)
            if (this.voucherRedeem.member_id && this.selectedMemberObj) {
                return this.selectedMemberObj;
            }
            if (this.voucherRedeem.member_id && this.members.length > 0) {
                return this.members.find(m => m.id === this.voucherRedeem.member_id || m.value === this.voucherRedeem.member_id);
            }
            return null;
        },
        isMemberPointsNegative() {
            return this.selectedMember && this.selectedMember.points < 0;
        },
        selectedVoucherType() {
            if (this.voucherRedeem.voucher_type) {
                return this.voucherRedeem.voucher_type;
            }
            const storeVouchers = store.state.Voucher.vouchers || [];
            if (this.voucherRedeem.voucher_id && storeVouchers.length > 0) {
                const voucher = storeVouchers.find(v => v.id === this.voucherRedeem.voucher_id || v.value === this.voucherRedeem.voucher_id);
                if (voucher && voucher.voucher_type) {
                    return voucher.voucher_type;
                }
            }
            if (this.selectedOption && this.selectedOption.voucher_type) {
                return this.selectedOption.voucher_type;
            }
            return null;
        },
        orderedDisplayMembers() {
            if (!this.selectedMemberObj || !this.selectedViaSearch || this.memberSearch) return this.displayMembers;
            const rest = this.displayMembers.filter(m => m.id !== this.selectedMemberObj.id);
            return [this.selectedMemberObj, ...rest];
        }
    },
    async mounted() {
        this.loadBusinessProfile();

        const id = this.redeemId || this.$route.params.id;
        if (id) {
            this.loadState(id);
        } else {
            Html5Qrcode.getCameras().then(devices => {
                if (devices && devices.length) this.cameraId = devices[0].id;
            }).catch(err => console.error('getCameras error', err));
            this.loadMemberInit();
        }
    },
    beforeUnmount() {
        try {
            if (this.scanner && this.scanner.clear) this.scanner.clear();
        } catch (e) {
            console.error('Failed to clear scanner on unmount', e);
        }
        if (this.searchDebounceTimer) clearTimeout(this.searchDebounceTimer);
    },
    methods:{
        disableLoading: function(){            
            this.isLoading = false;
        },
        getInitials(name) {
            if (!name) return '?';
            return name.split(' ').slice(0, 2).map(n => n[0]).join('').toUpperCase();
        },
        mapMember(m) {
            return {
                ...m,
                full_name: `${m.title || ''} ${m.first_name || ''} ${m.last_name || ''}`.trim(),
                email: m.user?.email || '',
                code: m.code || '',
                photo: m.photo || m.user?.user_profile?.avatar?.image_url || null,
                imageError: false,
            };
        },
        selectMember(member) {
            this.voucherRedeem.member_id = member.id;
            this.voucherRedeem.member_name = member.full_name;
            this.selectedMemberObj = { ...member };
            this.selectedViaSearch = !!(this.memberSearch && this.memberSearch.trim());
        },
        async loadMemberInit() {
            this.membersLoaded = false;
            this.displayMembers = [];
            store.commit('Member/reset_meta');
            store.commit('Member/switch_page', { meta: { page: 1, limit: 10 } });
            try {
                const bp = await store.dispatch('Auth/getBusinessProfile');
                this.corporateId = bp.corporate_id;
                store.commit('Member/add_filter', { filter: { corporate_id: this.corporateId } });
                await store.dispatch('Member/read');
                this.displayMembers = (store.state.Member.members || []).map(this.mapMember);
                this.membersLoaded = true;
            } catch (e) {
                console.error('Failed to load members', e);
                this.membersLoaded = true;
            }
        },
        async clearMemberSearch() {
            this.memberSearch = '';
            store.commit('Member/clear_search');
            await this.loadMemberInit();
        },
        async handleMemberScroll() {
            const el = this.$refs.membersListRef;
            if (!el || this.loadingMore) return;
            if (this.memberSearch && this.memberSearch.trim() !== '') return;
            const distanceToBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
            if (distanceToBottom < 100) {
                const meta = store.state.Member.meta || {};
                const currentPage = Number(meta.page) || 1;
                const limit = Number(meta.limit) || 20;
                const totalPages = Number(meta.total) || 0;
                if (currentPage < totalPages) {
                    this.loadingMore = true;
                    const nextMeta = { ...meta, page: currentPage + 1, limit: limit };
                    store.commit('Member/switch_page', { meta: nextMeta });
                    try {
                        await store.dispatch('Member/read');
                        const newMembers = (store.state.Member.members || []).map(this.mapMember);
                        this.displayMembers = [...this.displayMembers, ...newMembers];
                    } catch (e) {
                        console.error('Failed to load more members', e);
                    } finally {
                        this.loadingMore = false;
                    }
                }
            }
        },
        startScan() {
            this.scanHidden = false;
            this.$nextTick(() => {
                if (!this.cameraId) return;
                this.scanner = new Html5Qrcode('wrap-scanner-redeem');
                const config = { fps: 4, qrbox: { width: '100%', height: '100%' } };
                this.scanner.start(
                    { deviceId: { exact: this.cameraId } },
                    config,
                    this.onScanSuccess
                ).catch(err => console.error('scanner.start failed', err));
            });
        },
        stopScan() {
            try {
                if (this.scanner && this.scanner.stop) this.scanner.stop();
            } catch (e) {
                console.error('Failed to stop scanner', e);
            }
            this.preloadAbort = true;
            this.scanHidden = true;
        },
        async onScanSuccess(decodedText) {
            let memberData;
            try {
                memberData = JSON.parse(decodedText);
            } catch (e) {
                this.error_scan.message = 'Invalid QR data';
                setTimeout(() => { this.error_scan.message = ''; }, 1000);
                return;
            }
            const found = (store.state.Member.members || []).find(m => String(m.id) === String(memberData.memberId));
            if (found) {
                this.displayMembers = [this.mapMember(found)];
                this.selectMember(this.mapMember(found));
                this.stopScan();
                return;
            }
            try {
                const res = await store.dispatch('Member/read_single', memberData.memberId);
                if (res) {
                    store.commit('Member/add_single_member', { member: res });
                    this.displayMembers = [this.mapMember(res)];
                    this.selectMember(this.mapMember(res));
                    this.stopScan();
                    return;
                }
                this.error_scan.message = 'Member not found';
                setTimeout(() => { this.error_scan.message = ''; }, 1000);
            } catch (err) {
                console.error('read_single failed', err);
                this.error_scan.message = 'Member not found';
                setTimeout(() => { this.error_scan.message = ''; }, 1000);
            }
        },
        async loadState(id){
            store.dispatch('VoucherRedeem/read_single',id).then((data)=>{
                    this.voucherRedeem.id = data.id
                    this.voucherRedeem.voucher_id = data.voucher_id
                    this.voucherRedeem.member_id = data.member_id
                    this.voucherRedeem.member_name = `${data.member.title} ${data.member.first_name} ${data.member.last_name}`;
                    this.voucherRedeem.voucher_name = data.voucher.name
                    this.voucherRedeem.voucher_type = data.voucher.voucher_type
                    this.voucherRedeem.voucher_merchant_name = data.voucher?.provider_name || data.voucher?.corporate_name || data.voucher?.merchant_name || data.voucher?.merchant?.name || ''
                    this.voucherRedeem.expired_at = dayjs(data.expired_at).format('YYYY-MM-DDTHH:mm')
                    this.voucherRedeem.claimed_at = data.claimed_at ? dayjs(data.claimed_at).format('YYYY-MM-DDTHH:mm') : ""
                    this.voucherRedeem.cardTitle = 'Claim Voucher'
                    this.voucherRedeem.claimed_at_buttom = true
                    this.voucherRedeem.redeem_claimed = false
                    this.voucherRedeem.disable = true;
                    
                    this.members = this.members.filter((v) => v.value == data.member_id);
                    this.vouchers = this.vouchers.filter((v) => v.value == data.voucher_id);
                    this.isLoading = false;
            })
        },
        loadBusinessProfile: function() {
            this.isLoading = true;
            store.dispatch('Auth/getBusinessProfile').then((businessProfile) => {
                this.selectedBusinessProfile.corporate_id = businessProfile.corporate_id;
                this.selectedBusinessProfile.branch_id = businessProfile.branch_id;
                this.selectedBusinessProfile.merchant_id = businessProfile.merchant_id;
                
                this.voucherRedeem.merchant_id = businessProfile.merchant_id;
            });
        },
        redeem: function(){
            if (this.selectedMember && this.selectedMember.points < 0) {
                this.error = 'Cannot redeem voucher. Member has negative points. Please complete transactions to make points positive first.';
                return;
            }

            this.validation = [];
            this.error = '';

            const data = {...this.voucherRedeem};
            if (data.quantity > 0 && data.quantity <= 10) {
                this.voucherRedeem.isLoading = true;
                
                if(!this.redeemId && this.$route.name != 'VoucherRedeemEdit'){
                    this.voucherRedeem.cardTitle = 'Create Voucher Redeem';
                
                    store.dispatch('VoucherRedeem/create', {data})
                    .then(() => {
                        this.$emit('success');
                    }).catch((err) => {                        
                        if (err.response && err.response.status === 400 && err.response.data.error.includes("Member not have enough point to redeem this voucher or voucher stock not available")) {
                            this.error = "Member not have enough point to redeem this voucher or voucher stock not available"
                        }

                        try {
                            const errorData = JSON.parse(err.response.data.message);
                            this.validation = errorData;
                        } catch (e) {
                            this.error = err.response.data.message;
                        }

                    }).finally(() =>{
                        this.voucherRedeem.isLoading = false
                    });
                }
            }
        },
        getPosQuery() {
            if (this.selectedMember) {
                const member = this.selectedMember;
                const title = member.title ? member.title + ' ' : '';
                const firstName = member.first_name ? member.first_name : '';
                const lastName = member.last_name ? ' ' + member.last_name : '';
                let name = (title + firstName + lastName).trim();
                if (member.user && member.user.email) {
                    name += ' (' + member.user.email + ')';
                }
                return {
                    member_id: member.id,
                    member_name: name
                };
            } else if (this.voucherRedeem.member_id) {
                return {
                    member_id: this.voucherRedeem.member_id,
                    member_name: this.voucherRedeem.member_name
                };
            }
            return {};
        },
        goToPOS() {
            this.$router.push({ name: 'TransactionForm', query: this.getPosQuery() });
        },
        redeemToPOS() {
            if (this.selectedMember && this.selectedMember.points < 0) {
                this.error = 'Cannot redeem voucher. Member has negative points. Please complete transactions to make points positive first.';
                return;
            }

            this.validation = [];
            this.error = '';

            const data = {...this.voucherRedeem};
            data.usage_type = 'pos';
            if (data.quantity > 0 && data.quantity <= 10) {
                this.voucherRedeem.isLoading = true;
                
                if(!this.redeemId && this.$route.name != 'VoucherRedeemEdit'){
                    this.voucherRedeem.cardTitle = 'Create Voucher Redeem';
                
                    const posQuery = this.getPosQuery();

                    store.dispatch('VoucherRedeem/create', {data})
                    .then(() => {
                        this.$emit('success');
                        this.$router.push({
                            name: 'TransactionForm',
                            query: posQuery
                        })
                    }).catch((err) => {                        
                        if (err.response && err.response.status === 400 && err.response.data.error.includes("Member not have enough point to redeem this voucher or voucher stock not available")) {
                            this.error = "Member not have enough point to redeem this voucher or voucher stock not available"
                        }

                        try {
                            const errorData = JSON.parse(err.response.data.message);
                            this.validation = errorData;
                        } catch (e) {
                            this.error = err.response.data.message;
                        }

                    }).finally(() =>{
                        this.voucherRedeem.isLoading = false
                    });
                }
            }
        },
        redeemClaim: function(){
            this.validation = [];
            this.error = '';
            const data = {...this.voucherRedeem};
            if (data.quantity > 0 && data.quantity <= 10) {
                this.voucherRedeem.isLoading = true;
                        
                if(!this.redeemId && this.$route.name != 'VoucherRedeemEdit'){
                    this.voucherRedeem.cardTitle = 'Create Voucher Redeem';
                
                    store.dispatch('VoucherRedeem/claim', {data}).then(() => {
                        this.$emit('success');
                    }).catch((err) => {
                        if (err.response && err.response.status === 400 && err.response.data.error.includes("Member not have enough point to redeem this voucher or voucher stock not available")) {
                            this.error = "Member not have enough point to redeem this voucher or voucher stock not available"                            
                        }

                        try {
                            const errorData = JSON.parse(err.response.data.message);
                            this.validation = errorData;
                        } catch (e) {
                            this.error = err.response.data.message;
                        }
                    }).finally(() =>{
                        this.voucherRedeem.isLoading = false
                    });
                }
            }
        },
        update: function(){
            const data = {...this.voucherRedeem};
            this.voucherRedeem.isLoading = true;
                        
            if(this.redeemId || this.$route.name != 'VoucherRedeemForm'){
                this.voucherRedeem.disable = true;
                this.voucherRedeem.cardTitle = 'Claim Voucher';
                this.voucherRedeem.isLoading = true;
                this.voucherRedeem.redeem_claimed = false;
                
                this.voucherRedeem.claimed_at = dayjs(new Date()).format('YYYY-MM-DDTHH:mm')
                
                store.dispatch('VoucherRedeem/update', {data}).then(() => {
                    this.$emit('success');
                }).catch((err) => {
                    if (err.response && err.response.status === 400) {
                        this.error = err.response?.data?.error || err.response?.data?.message || 'Failed to claim voucher';
                    } else {
                        this.error = err.response?.data?.message || 'Failed to claim voucher';
                    }
                }).finally(() =>{
                    this.voucherRedeem.isLoading = false
                });
            }
        },
        handleClaimVoucher: function() {
            if (this.selectedMember && this.selectedMember.points < 0) {
                this.error = 'Cannot claim voucher. Member has negative points. Please complete transactions to make points positive first.';
                return;
            }

            this.update();
        },
        handleManualClaimCreditDiscount: function() {
            if (this.selectedMember && this.selectedMember.points < 0) {
                this.error = 'Cannot claim voucher. Member has negative points.';
                return;
            }

            this.validation = [];
            this.error = '';

            const data = { ...this.voucherRedeem };
            data.usage_type = 'manual';

            this.voucherRedeem.isLoading = true;

            store.dispatch('VoucherRedeem/update', { data })
                .then(() => {
                    this.$emit('success');
                })
                .catch((err) => {
                    if (err.response && err.response.status === 400) {
                        this.error = err.response.data.error || err.response.data.message;
                    } else {
                        this.error = err.response?.data?.message || 'Failed to claim voucher';
                    }
                })
                .finally(() => {
                    this.voucherRedeem.isLoading = false;
                });
        },
        getExpired: function (selectedOpt) {
            const voucherId = (selectedOpt && selectedOpt.id) || this.voucherRedeem.voucher_id;
            if (voucherId) {
                store.dispatch('Voucher/read_single', voucherId).then((response)=>{
                    this.selectedOption = response;
                    if (response && response.voucher_type) {
                        this.voucherRedeem.voucher_type = response.voucher_type;
                    }
                }).catch((err) => {
                    console.error('Failed to fetch single voucher info', err);
                });
            }
        },
        handleCancel() {
            this.$emit('cancel');
        }
    },
    watch: {
        memberSearch(val) {
            if (this.searchDebounceTimer) clearTimeout(this.searchDebounceTimer);
            this.searchDebounceTimer = setTimeout(async () => {
                const query = (val || '').trim();
                if (query === '') {
                    store.commit('Member/clear_search');
                    await this.loadMemberInit();
                    return;
                }
                try {
                    this.membersLoaded = false;
                    this.displayMembers = [];
                    await store.dispatch('Member/all', this.corporateId);
                    const all = store.state.Member.members || [];
                    const q = query.toLowerCase();
                    this.displayMembers = all
                        .filter(m => {
                            const name = `${m.title || ''} ${m.first_name || ''} ${m.last_name || ''}`.toLowerCase();
                            const email = (m.user?.email || '').toLowerCase();
                            return name.includes(q) || email.includes(q);
                        })
                        .map(this.mapMember);
                    this.membersLoaded = true;
                } catch (e) {
                    console.error('member search failed', e);
                    await this.loadMemberInit();
                }
            }, 350);
        }
    }
}
</script>

<!-- Non-scoped: required because OModal teleports to <body>, scoped CSS cannot reach teleported elements -->
<style>
    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');

    /* Scan Overlay — z-index 10000 to appear above OModal (z-index: 9999) */
    .scan-overlay {
        position: fixed;
        top: 0;
        left: 0;
        width: 100vw;
        height: 100vh;
        background: rgba(0, 0, 0, 0.5);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 10000;
    }

    .scan-modal-box {
        background: #fff;
        border-radius: 12px;
        width: 420px;
        max-width: 95vw;
        box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
        overflow: hidden;
    }

    .scan-modal-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 16px 20px;
        border-bottom: 1px solid #e5e7eb;
    }

    .scan-modal-title {
        font-family: 'Inter', sans-serif;
        font-weight: 600;
        font-size: 16px;
        color: #1f2937;
    }

    .scan-modal-close {
        background: transparent;
        border: none;
        color: #9ca3af;
        font-size: 18px;
        cursor: pointer;
        padding: 4px;
        line-height: 1;
    }

    .scan-modal-close:hover {
        color: #ef4444;
    }

    .scan-modal-body {
        padding: 20px;
        text-align: center;
    }

    .scan-message {
        margin-top: 10px;
        padding: 8px 12px;
        background: #fff5f5;
        border: 1px solid #fecaca;
        border-radius: 6px;
        color: #dc2626;
        font-size: 13px;
        text-align: center;
    }

    .scan-modal-footer {
        padding: 12px 20px;
        border-top: 1px solid #e5e7eb;
        text-align: center;
    }

    .btn-stop-scan {
        background: var(--primary-color, #F27178);
        border: 1px solid var(--primary-color, #F27178);
        color: white;
        padding: 10px 24px;
        border-radius: 8px;
        font-family: 'Inter', sans-serif;
        font-weight: 600;
        font-size: 14px;
        cursor: pointer;
        transition: filter 0.15s;
    }

    .btn-stop-scan:hover {
        filter: brightness(0.9);
    }

    /* Wide Modal Override — mirrors InjectPointForm .inject-modal-wide */
    .redeem-modal-wide {
        max-width: 872px !important;
        width: 95vw !important;
        font-family: 'Inter', sans-serif;
        max-height: 90vh !important;
        display: flex !important;
        flex-direction: column !important;
    }

    .redeem-modal-wide .omni-modal-body {
        overflow: hidden !important;
        display: flex !important;
        flex-direction: column !important;
        flex: 1 !important;
        min-height: 0 !important;
        padding: 16px 24px 20px 24px !important;
    }

    .redeem-modal-wide .form-group {
        margin-bottom: 0 !important;
    }

    /* Responsive — Tablet (max 900px) */
    @media (max-width: 900px) {
        .redeem-modal-wide {
            max-width: 98vw !important;
            width: 98vw !important;
            max-height: 92vh !important;
        }

        .redeem-modal-wide .omni-modal-body {
            overflow-y: auto !important;
        }
    }

    /* Responsive — Mobile (max 576px) */
    @media (max-width: 576px) {
        .redeem-modal-wide {
            max-width: 100vw !important;
            width: 100vw !important;
            max-height: 95vh !important;
            margin: 0 !important;
        }

        .redeem-modal-wide .omni-modal-body {
            padding: 12px 14px 14px 14px !important;
            overflow-y: auto !important;
        }
    }
</style>

<style scoped>
    /* Two Column Layout — fixed container framed neatly inside modal */
    .two-col-layout {
        display: flex;
        flex-direction: row;
        align-items: stretch;
        gap: 0;
        border: 1px solid #D9D9D9;
        border-radius: 12px;
        overflow: hidden;
        height: 440px;
        max-height: calc(85vh - 160px);
    }

    .two-col-layout * {
        box-sizing: border-box;
    }

    /* Left panel — fixed width, container doesn't scroll, list inside scrolls */
    .left-panel {
        width: 400px;
        flex-shrink: 0;
        background: #F9F9F9;
        border-radius: 12px 0 0 12px;
        padding: 20px;
        display: flex;
        flex-direction: column;
        gap: 12px;
        overflow: hidden;
        height: 100%;
    }

    /* Right panel — clean fixed panel */
    .right-panel {
        flex: 1;
        background: #FFFFFF;
        border-radius: 0 12px 12px 0;
        padding: 20px 24px;
        display: flex;
        flex-direction: column;
        gap: 14px;
        overflow-y: auto;
        height: 100%;
    }

    /* Panel label — matches InjectPointForm */
    .panel-title {
        font-family: 'Inter', sans-serif;
        font-weight: 700;
        font-size: 14px;
        line-height: 20px;
        color: #404040;
        margin: 0 0 8px 0;
        margin-bottom: 0px !important;
    }

    /* Form section & sub-item spacing */
    .form-section,
    .form-sub-item {
        display: flex;
        flex-direction: column;
        gap: 6px;
    }

    /* Modern Fintech Label & Error Styling — matching VoucherForm.vue */
    .form-sub-item label {
        display: block;
        font-size: 12px;
        font-weight: 600;
        color: #475569;
        margin-bottom: 2px;
        text-transform: uppercase;
        letter-spacing: 0.5px;
    }

    .required {
        color: #ef4444;
    }

    .error-text {
        color: #ef4444;
        font-size: 12px;
        margin-top: 4px;
        font-weight: 500;
    }

    /* Reset generic input overrides inside vue-select */
    :deep(.vs__search),
    :deep(.vs__search:focus) {
        width: auto !important;
        flex: 1 !important;
        padding: 0 !important;
        margin: 0 !important;
        border: none !important;
        border-radius: 0 !important;
        background-color: transparent !important;
        box-shadow: none !important;
        font-size: 14px !important;
        color: #0f172a !important;
        outline: none !important;
        height: auto !important;
        line-height: 1.4 !important;
    }

    /* Vue-Select Modern Overrides — matching VoucherForm.vue & fixed height alignment */
    :deep(.modern-v-select) {
        --vs-border-color: #cbd5e1;
        --vs-border-radius: 8px;
        --vs-font-size: 14px;
        --vs-colors--light: #64748b;
        margin: 0 !important;
    }

    :deep(.modern-v-select .vs__dropdown-toggle) {
        background-color: #f8fafc;
        border: 1.5px solid var(--vs-border-color);
        border-radius: var(--vs-border-radius);
        padding: 0 12px !important;
        height: 44px !important;
        min-height: 44px !important;
        max-height: 44px !important;
        display: flex !important;
        align-items: center !important;
        margin: 0 !important;
        transition: all 0.2s ease;
        box-sizing: border-box !important;
    }

    :deep(.modern-v-select .vs__selected-options) {
        display: flex !important;
        align-items: center !important;
        flex: 1 !important;
        min-width: 0 !important;
        padding: 0 !important;
        margin: 0 !important;
        height: 100% !important;
    }

    :deep(.modern-v-select .vs__selected) {
        display: flex !important;
        align-items: center !important;
        height: 100% !important;
        margin: 0 !important;
        padding: 0 !important;
        border: none !important;
        font-family: 'Inter', sans-serif;
        font-size: 14px !important;
        color: #0f172a !important;
        line-height: 1 !important;
        white-space: nowrap !important;
        overflow: hidden !important;
        text-overflow: ellipsis !important;
        max-width: 100% !important;
        position: relative !important;
    }

    :deep(.modern-v-select.vs--open .vs__dropdown-toggle),
    :deep(.modern-v-select .vs__dropdown-toggle:focus-within) {
        background-color: #ffffff;
        border-color: var(--primary-color, #F27178);
        box-shadow: 0 0 0 3px var(--secondary-color, rgba(242, 113, 120, 0.12));
    }

    :deep(.modern-v-select .vs__search::placeholder),
    :deep(.modern-v-select .vs__dropdown-toggle),
    :deep(.modern-v-select .vs__dropdown-menu) {
        font-family: 'Inter', sans-serif;
        font-size: 14px;
        color: #334155;
    }

    :deep(.modern-v-select .vs__actions) {
        padding: 0 !important;
        margin: 0 !important;
        display: flex !important;
        align-items: center !important;
        height: 100% !important;
        gap: 6px;
    }

    :deep(.modern-v-select .vs__clear) {
        margin: 0 !important;
        padding: 0 !important;
        line-height: 1;
        display: flex;
        align-items: center;
        fill: #94a3b8;
    }

    :deep(.modern-v-select .vs__open-indicator) {
        margin: 0 !important;
        padding: 0 !important;
        line-height: 1;
        display: flex;
        align-items: center;
        fill: #94a3b8;
    }

    :deep(.modern-v-select .vs__dropdown-menu) {
        border-color: #e2e8f0;
        box-shadow: 0 4px 6px -1px var(--secondary-color, rgba(0, 0, 0, 0.1)), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
        border-radius: 8px;
        margin-top: 4px;
        padding: 4px 0;
        z-index: 1000;
    }

    :deep(.modern-v-select .vs__dropdown-option) {
        padding: 8px 16px;
        color: #334155;
    }

    :deep(.modern-v-select .vs__dropdown-option--highlight) {
        background: var(--primary-color, #F27178);
        color: #ffffff;
    }

    /* Modern Input Styling Overrides — matching VoucherForm.vue */
    :deep(.input-wrapper),
    :deep(.o-input-text) {
        margin: 0 !important;
    }

    :deep(input.form-control),
    :deep(.input-wrapper input) {
        width: 100%;
        padding: 11px 14px !important;
        border: 1.5px solid #e2e8f0 !important;
        border-radius: 8px !important;
        font-size: 14px !important;
        color: #0f172a !important;
        background-color: #f8fafc !important;
        transition: all 0.2s;
    }

    :deep(input.form-control:focus),
    :deep(.input-wrapper input:focus) {
        border-color: var(--primary-color, #F27178) !important;
        background-color: #ffffff !important;
        box-shadow: 0 0 0 3px var(--secondary-color, rgba(242, 113, 120, 0.12)) !important;
        outline: none !important;
    }

    :deep(input.form-control:disabled),
    :deep(input.form-control[readonly]),
    :deep(.input-wrapper input:disabled),
    :deep(.input-wrapper input[readonly]) {
        background-color: #f1f5f9 !important;
        color: #64748b !important;
        border-color: #e2e8f0 !important;
        cursor: not-allowed;
    }

    /* Member Search */
    .member-search-row {
        display: flex;
        flex-direction: row;
        align-items: center;
        gap: 12px;
    }

    .member-search-wrap {
        flex: 1;
        display: flex;
        flex-direction: row;
        align-items: center;
        padding: 10px 14px;
        gap: 8px;
        background: #FFFFFF;
        border: 1px solid #D4D4D4;
        box-shadow: 0px 1px 2px rgba(0, 0, 0, 0.05);
        border-radius: 8px;
        transition: border-color 0.15s, box-shadow 0.15s;
        height: 44px;
    }

    .member-search-wrap:focus-within {
        border-color: var(--primary-color, #F27178);
        box-shadow: 0 0 0 3px var(--secondary-color, rgba(242, 113, 120, 0.12));
    }

    .search-icon {
        color: #737373;
        font-size: 14px;
        flex-shrink: 0;
    }

    .member-search-input {
        flex: 1;
        border: none;
        outline: none;
        padding: 0;
        font-family: 'Inter', sans-serif;
        font-weight: 400;
        font-size: 14px;
        color: #404040;
        background: transparent;
    }

    .member-search-input::placeholder {
        color: #737373;
    }

    .search-clear-btn {
        background: none;
        border: none;
        color: #9ca3af;
        cursor: pointer;
        font-size: 16px;
        line-height: 1;
        padding: 0;
        flex-shrink: 0;
    }

    .search-clear-btn:hover {
        color: #ef4444;
    }

    /* Scan button */
    .btn-scan {
        width: 52px;
        height: 44px;
        flex-shrink: 0;
        background: var(--primary-color, #F27178);
        border: 1px solid var(--primary-color, #F27178);
        box-shadow: 0px 1px 2px rgba(0, 0, 0, 0.05);
        border-radius: 8px;
        color: white;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 16px;
        transition: filter 0.15s;
    }

    .btn-scan:hover {
        filter: brightness(0.9);
    }

    /* Member List */
    .member-list-scroll {
        flex: 1;
        display: flex;
        flex-direction: column;
        gap: 12px;
        overflow-y: auto;
        max-height: 400px;
    }

    .member-list-scroll::-webkit-scrollbar {
        width: 4px;
    }

    .member-list-scroll::-webkit-scrollbar-track {
        background: transparent;
    }

    .member-list-scroll::-webkit-scrollbar-thumb {
        background: #D4D4D4;
        border-radius: 4px;
    }

    .member-list-empty {
        text-align: center;
        color: #737373;
        font-size: 14px;
        padding: 24px 0;
    }

    .member-card {
        display: flex;
        flex-direction: row;
        align-items: flex-start;
        padding: 16px;
        gap: 4px;
        background: #FFFFFF;
        border: 1px solid #E5E5E5;
        border-radius: 12px;
        cursor: pointer;
        transition: border-color 0.15s;
        flex-shrink: 0;
        margin: 0 !important;
    }

    .member-card:hover {
        border-color: var(--primary-color, #F27178);
    }

    .member-card.selected {
        border-color: var(--primary-color, #F27178);
    }

    .member-card-content {
        display: flex;
        flex-direction: row;
        align-items: flex-start;
        gap: 12px;
        flex: 1;
        min-width: 0;
    }

    .member-avatar {
        width: 42px;
        height: 42px;
        border-radius: 50%;
        overflow: hidden;
        flex-shrink: 0;
        background: var(--secondary-color, #FEE9E9);
        display: flex;
        align-items: center;
        justify-content: center;
    }

    .avatar-img {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }

    .avatar-initials {
        font-size: 13px;
        font-weight: 700;
        color: var(--primary-color, #F27178);
    }

    .member-info {
        flex: 1;
        min-width: 0;
        display: flex;
        flex-direction: column;
        gap: 2px;
    }

    .member-name {
        font-family: 'Inter', sans-serif;
        font-weight: 700;
        font-size: 14px;
        line-height: 20px;
        color: #404040;
        margin: 0;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    .member-email {
        font-family: 'Inter', sans-serif;
        font-weight: 400;
        font-size: 14px;
        line-height: 20px;
        color: #525252;
        margin: 0;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    .member-code {
        font-family: 'Inter', sans-serif;
        font-weight: 700;
        font-size: 13px;
        color: var(--primary-color, #F27178);
        margin: 0;
    }

    .member-check-wrap {
        width: 20px;
        height: 20px;
        flex-shrink: 0;
        display: flex;
        align-items: center;
        justify-content: center;
    }

    .member-check {
        width: 20px;
        height: 20px;
        background: var(--primary-color, #F27178);
        border: 1px solid var(--primary-color, #F27178);
        border-radius: 10px;
        display: flex;
        align-items: center;
        justify-content: center;
        color: white;
        font-size: 11px;
    }

    .member-radio {
        width: 16px;
        height: 16px;
        background: #FFFFFF;
        border: 1px solid #D4D4D4;
        border-radius: 8px;
    }

    /* Error Alert */
    .error-alert-footer {
        display: flex;
        gap: 8px;
        background: #fff5f5;
        border: 1px solid #fecaca;
        border-radius: 8px;
        padding: 8px 12px;
        align-items: center;
        flex: 1;
        min-width: 0;
    }

    .error-alert-icon {
        color: #ef4444;
        font-size: 14px;
        flex-shrink: 0;
    }

    .error-alert-title {
        font-size: 13px;
        font-weight: 600;
        color: #b91c1c;
        margin: 0;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    /* Footer Actions — 1 baris 2 kolom (Left: Error alert, Right: Action buttons) */
    .form-footer {
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        align-items: center;
        gap: 12px;
        padding: 16px 24px 20px 24px;
        margin-left: -24px;
        margin-right: -24px;
        margin-bottom: -20px;
        margin-top: 16px;
        background: #ffffff;
        z-index: 10;
        border-top: 1px solid #E5E5E5;
        flex-shrink: 0;
    }

    .footer-actions {
        display: flex;
        flex-direction: row;
        align-items: center;
        gap: 8px;
        margin-left: auto;
        flex-shrink: 0;
    }

    /* Cancel button — matches InjectPointForm .btn-cancel */
    .btn-cancel {
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 8px 14px;
        height: 38px;
        min-width: 76px;
        background: #FFFFFF;
        border: 1px solid #D4D4D4;
        box-shadow: 0px 1px 2px rgba(0, 0, 0, 0.05);
        border-radius: 8px;
        font-family: 'Inter', sans-serif;
        font-weight: 600;
        font-size: 13px;
        color: #404040;
        cursor: pointer;
        transition: background 0.15s;
        white-space: nowrap;
    }

    .btn-cancel:hover {
        background: #f9fafb;
    }

    /* Confirm button — matches InjectPointForm .btn-confirm */
    .btn-confirm {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 6px;
        padding: 8px 14px;
        height: 38px;
        min-width: 85px;
        background: var(--primary-color, #F27178);
        border: 1px solid var(--primary-color, #F27178);
        box-shadow: 0px 1px 2px rgba(0, 0, 0, 0.05);
        border-radius: 8px;
        font-family: 'Inter', sans-serif;
        font-weight: 600;
        font-size: 13px;
        color: #FFFFFF;
        cursor: pointer;
        transition: filter 0.15s;
        white-space: nowrap;
    }

    .btn-confirm:hover:not(:disabled) {
        filter: brightness(0.9);
    }

    .btn-confirm:disabled {
        opacity: 0.65;
        cursor: not-allowed;
    }

    /* Dynamic theme variant: Bottom navbar color for Redeem & Manual Claimed / Redeem & Claimed */
    .btn-confirm-success {
        background: var(--bottom, #663434);
        border-color: var(--bottom, #663434);
        color: var(--text-navbar, #FFFFFF);
    }

    /* Dynamic theme variant: Top navbar color for Redeem & Go to POS */
    .btn-confirm-pos {
        background: var(--top, #FC6666);
        border-color: var(--top, #FC6666);
        color: var(--text-navbar, #FFFFFF);
    }

    /* Legacy */
    input[type="radio"] {
        accent-color: var(--primary-color, #F27178);
    }

    .form-control:focus {
        border-color: var(--primary-color, #F27178);
        box-shadow: 0 0 0 0.2rem rgba(242, 113, 120, 0.2);
    }

    /* Responsive — Tablet (max 900px) */
    @media (max-width: 900px) {
        .two-col-layout {
            flex-direction: column;
            min-height: unset;
            border-radius: 12px;
        }

        .left-panel {
            width: 100%;
            border-radius: 12px 12px 0 0;
            max-height: 340px;
            overflow-y: auto;
            flex-shrink: 0;
        }

        .right-panel {
            border-radius: 0 0 12px 12px;
            max-height: unset;
            overflow-y: visible;
        }
    }

    /* Responsive — Mobile (max 576px) */
    @media (max-width: 576px) {
        .left-panel {
            padding: 16px;
            max-height: 280px;
        }

        .right-panel {
            padding: 16px;
            gap: 16px;
        }

        .form-footer {
            padding-top: 12px;
        }

        .btn-cancel,
        .btn-confirm {
            font-size: 14px;
            padding: 10px 14px;
        }
    }
</style>